import type { AnonCart, AnonCartItem } from "../../model/AnonCartTypes";
import type { IntlNotification } from "../../utils/notifications";
import type { CartPrices } from "../../model/Prices";
import { floorToThreeDecimalPlaces, minQuantity } from "../../utils/NumberUtil";
import { intlRoot } from "../../IntlRootMixin";
import { ServiceError } from "../BaseTypes";
export interface AnonCartEvent {
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;
	type: string;
	applyTo(cart: AnonCart): AnonCart | null | undefined;
	getErrorMessage(): IntlNotification | null | undefined;
	getParams(): string;
}
export type EventStatus = {
	type: "Pending" | "Failure" | "Success";
	reason?:
		| "DUPLICATE"
		| "CART_NOT_FOUND"
		| "CART_FULL"
		| "INVALID_STATE"
		| "INVALID_ITEM"
		| "INVALID_QUANTITY"
		| "TOO_OPTIMISTIC";
};
export class CartCreatedEvent implements AnonCartEvent {
	type = "CartCreatedEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;

	constructor(
		eventId: string,
		cartId: string,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
	}

	applyTo(cart: AnonCart) {
		return { ...cart, cartVersion: 0 };
	}

	getErrorMessage(): IntlNotification | null | undefined {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CREATE_CART" }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return "()";
	}
}
export class CartDeletedEvent implements AnonCartEvent {
	type = "CartDeletedEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;

	constructor(
		eventId: string,
		cartId: string,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
	}

	applyTo() {
		return null;
	}

	getErrorMessage(): IntlNotification | null {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_DELETE_CART" }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return "()";
	}
}
export class CartMergedEvent implements AnonCartEvent {
	type = "CartMergedEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;

	constructor(
		eventId: string,
		cartId: string,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
	}

	applyTo() {
		return null;
	}

	getErrorMessage(): IntlNotification | null {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CART_MERGE" }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return "()";
	}
}

function updatePrices(cart: AnonCart, cartPrices: CartPrices): AnonCart {
	const items = Object.fromEntries(
		Object.entries(cart.items).map(([key, item]) => [
			key,
			{
				...item,
				prices: cartPrices.articlePrices?.[item.bundleId],
			},
		]),
	);

	return { ...cart, totalPrices: cartPrices.total, items };
}

export class ItemQuantitySetEvent implements AnonCartEvent {
	type = "ItemQuantitySetEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;
	bundleId: string;
	quantity: number;
	prices: CartPrices | null | undefined;

	constructor(
		eventId: string,
		cartId: string,
		bundleId: string,
		quantity: number,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
		prices?: CartPrices | null | undefined,
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
		this.bundleId = bundleId;
		this.quantity = quantity;
		this.prices = prices;
	}

	applyTo(cart: AnonCart): AnonCart {
		if (this.status.type === "Failure") {
			return addToHistory(this, cart);
		}

		if (this.prices) {
			cart = updatePrices(cart, this.prices);
		}

		const quantity = floorToThreeDecimalPlaces(this.quantity);

		if (!cart.items[this.bundleId]) {
			return _addToCart(cart, this.bundleId, quantity, this.status, new Date().getTime(), this.prices);
		}

		if (quantity < minQuantity) {
			const newItems = { ...cart.items };
			delete newItems[this.bundleId];
			return { ...cart, items: newItems, cartVersion: cart.cartVersion + 1 };
		}

		const items = {
			...cart.items,
			[this.bundleId]: {
				...cart.items[this.bundleId],
				quantity,
				unconfirmedState: this.status.type === "Pending",
			},
		};
		return { ...cart, items, cartVersion: cart.cartVersion + 1 };
	}

	getErrorMessage(): IntlNotification | null | undefined {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SET_ITEM_QUANTITY" }, { articleId: this.bundleId }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return `(itemId="${this.bundleId}", qty=${this.quantity})`;
	}
}
export class ItemDeletedEvent implements AnonCartEvent {
	type = "ItemDeletedEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;
	bundleId: string;
	prices: CartPrices | null | undefined;

	constructor(
		eventId: string,
		cartId: string,
		bundleId: string,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
		prices?: CartPrices | null | undefined,
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
		this.bundleId = bundleId;
		this.prices = prices;
	}

	applyTo(cart: AnonCart) {
		if (this.status.type === "Failure") {
			return addToHistory(this, cart);
		}

		if (this.prices) {
			cart = updatePrices(cart, this.prices);
		}

		const newItems = { ...cart.items };
		delete newItems[this.bundleId];
		return { ...cart, items: newItems, cartVersion: cart.cartVersion + 1 };
	}

	getErrorMessage(): IntlNotification | null | undefined {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			id: this.eventId,
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_DELETE_ITEM" }, { articleId: this.bundleId }),
		};
	}

	getParams() {
		return `(itemId="${this.bundleId}")`;
	}
}
export class ItemAddedEvent implements AnonCartEvent {
	type = "ItemAddedEvent";
	eventId: string;
	cartId: string;
	seqNum: number;
	status: EventStatus;
	bundleId: string;
	quantity: number;
	addedTimestamp: number;
	prices: CartPrices | null | undefined;

	constructor(
		eventId: string,
		cartId: string,
		bundleId: string,
		quantity: number,
		addedTimestamp: number,
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
		prices?: CartPrices | null | undefined,
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.seqNum = seqNum;
		this.status = status;
		this.bundleId = bundleId;
		this.quantity = quantity;
		this.addedTimestamp = addedTimestamp;
		this.prices = prices;
	}

	applyTo(cart: AnonCart) {
		if (this.status.type === "Failure") {
			return addToHistory(this, cart);
		}

		return _addToCart(
			cart,
			this.bundleId,
			floorToThreeDecimalPlaces(this.quantity),
			this.status,
			this.addedTimestamp,
			this.prices,
		);
	}

	getErrorMessage(): IntlNotification | null | undefined {
		if (this.status.type !== "Failure") {
			return null;
		}

		if (this.status.reason === "CART_FULL") {
			return {
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CART_FULL" }, { articleId: this.bundleId }),
				id: this.eventId,
			};
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_ADD_ITEM" }, { articleId: this.bundleId }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return `(itemId="${this.bundleId}", qty=${this.quantity})`;
	}
}

function _addToCart(
	cart: AnonCart,
	bundleId: string,
	quantity: number,
	status: EventStatus,
	addedTimestamp: number,
	prices: CartPrices | null | undefined,
): AnonCart {
	if (quantity < minQuantity) {
		return cart;
	}

	const blankItem: AnonCartItem = createItem(bundleId, quantity, addedTimestamp, status.type === "Pending");
	const updatedCart: AnonCart = {
		...cart,
		items: { ...cart.items, [bundleId]: blankItem },
		cartVersion: cart.cartVersion + 1,
	};

	if (prices) {
		return updatePrices(updatedCart, prices);
	}

	return updatedCart;
}

export class ItemServiceOptionsSetEvent implements AnonCartEvent {
	type = "ItemServiceOptionsSetEvent";
	eventId: string;
	cartId: string;
	bundleId: string;
	serviceOptionIds: string[];
	seqNum: number;
	status: EventStatus;
	prices: CartPrices | null | undefined;

	constructor(
		eventId: string,
		cartId: string,
		bundleId: string,
		serviceOptionIds: string[],
		seqNum = -1,
		status: EventStatus = {
			type: "Pending",
		},
		prices?: CartPrices | null | undefined,
	) {
		this.eventId = eventId;
		this.cartId = cartId;
		this.bundleId = bundleId;
		this.serviceOptionIds = serviceOptionIds;
		this.seqNum = seqNum;
		this.status = status;
		this.prices = prices;
	}

	applyTo(cart: AnonCart): AnonCart {
		if (this.status.type === "Failure") {
			return addToHistory(this, cart);
		}

		if (this.prices) {
			cart = updatePrices(cart, this.prices);
		}

		return {
			...cart,
			cartVersion: cart.cartVersion + 1,
			items: {
				...cart.items,
				[this.bundleId]: { ...cart.items[this.bundleId], selectedServiceOptions: this.serviceOptionIds },
			},
		};
	}

	getErrorMessage(): IntlNotification | null | undefined {
		if (this.status.type !== "Failure") {
			return null;
		}

		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_" }, { articleId: this.bundleId }),
			id: this.eventId,
		};
	}

	getParams(): string {
		return `(itemId="${this.bundleId}", serviceOptionIds="${String(this.serviceOptionIds)}")`;
	}
}
export function addToHistory(event: AnonCartEvent, cart: AnonCart): AnonCart {
	return { ...cart, eventHistory: [...cart.eventHistory, event.eventId] };
}
export function applyEvents(initialState: AnonCart, events: Array<AnonCartEvent>): AnonCart | null | undefined {
	// @ts-expect-error
	return events
		.filter((event) => event.status.type !== "Failure")
		.reduce(
			(cart, event) =>
				// @ts-expect-error
				cart ? event.applyTo(cart) : null,
			initialState,
		);
}

function createItem(
	bundleId: string,
	quantity: number,
	addedTimestamp: number,
	unconfirmedState: boolean,
): AnonCartItem {
	return {
		bundleId,
		quantity,
		addedTimestamp,
		errors: [],
		selectedServiceOptions: [],
		customerBuyable: false,
		employeeBuyable: false,
		prices: null,
		outdated: true,
		serviceOptions: [],
		availability: "UNKNOWN",
		availableStock: 999,
		stockMustBeChecked: null,
		categories: [],
		chargedByWeight: false,
		description: bundleId,
		imageSources: {},
		missingReasons: ["PENDING"],
		unconfirmedState,
	};
}

export function isEventPending(event: AnonCartEvent): boolean {
	return event.status.type === "Pending";
}
export function parseLocalEvent(jsonObject: Record<string, any>): AnonCartEvent {
	return parseEvent(jsonObject.eventId, jsonObject.cartId, jsonObject.seqNum, jsonObject.status, jsonObject);
}
export function parseServerEvent(jsonObject: Record<string, any>): AnonCartEvent {
	return parseEvent(jsonObject.eventId, jsonObject.context.cartId, jsonObject.seqNum, jsonObject.status, jsonObject);
}

function parseEvent(
	eventId: string,
	cartId: string,
	seqNum: number,
	status: EventStatus,
	eventJson: Record<string, any>,
): AnonCartEvent {
	switch (eventJson.type) {
		case "CartCreatedEvent":
			return new CartCreatedEvent(eventId, cartId, seqNum, status);

		case "CartDeletedEvent":
			return new CartDeletedEvent(eventId, cartId, seqNum, status);

		case "CartMergedEvent":
			return new CartMergedEvent(eventId, cartId, seqNum, status);

		case "ItemAddedEvent": {
			return new ItemAddedEvent(
				eventId,
				cartId,
				eventJson.bundleId,
				eventJson.quantity,
				eventJson.addedTimestamp,
				seqNum,
				status,
				eventJson.prices,
			);
		}

		case "ItemDeletedEvent": {
			return new ItemDeletedEvent(eventId, cartId, eventJson.bundleId, seqNum, status, eventJson.prices);
		}

		case "ItemQuantitySetEvent": {
			return new ItemQuantitySetEvent(
				eventId,
				cartId,
				eventJson.bundleId,
				eventJson.quantity,
				seqNum,
				status,
				eventJson.prices,
			);
		}

		case "ItemServiceOptionsSetEvent": {
			return new ItemServiceOptionsSetEvent(
				eventId,
				cartId,
				eventJson.bundleId,
				eventJson.serviceOptionIds,
				seqNum,
				status,
				eventJson.prices,
			);
		}

		default:
			throw new ServiceError({
				code: "OC-804",
				message: `Could not parse incoming event: ${JSON.stringify(eventJson)}`,
				type: "ui-error",
			});
	}
}
